






































































































































































































import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import { IPartnerImage, PartnerImage } from "@/models/partner-image.entity";
import { IVideo, Video } from "@/models/video.entity";
import ConfirmActionDialog from "./ConfirmActionDialog.vue";
import MediaGalleryFullscreen, { MediaGalleryItemType, MediaGalleryFullscreenItem } from "./MediaGalleryFullscreen.vue";
import MediaGalleryItem from "./MediaGalleryItem.vue";
import { ILead } from "@/models/lead.entity";
import { handleError } from "@/lib/utility/handleError";
import { debounce } from "debounce";

type IHasPartnerId = Pick<ILead, "partnerId">;
type IHasImages = Pick<ILead, "images">;
type IHasImageIds = Pick<ILead, "imageIds">;
type IHasVideoIds = Pick<ILead, "videoIds">;
type IHasVideos = Pick<ILead, "videos">;
type IHasId = Pick<ILead, "id">;
type HasUpdateMedia<T> = { updatePartial: (dto: Partial<IHasImageIds & IHasVideoIds>) => Promise<T> };

interface ITempItem {
  _tempKey: string;
  medium: IPartnerImage | IVideo;
}

type ITempImageItem = ITempItem & { medium: IPartnerImage };
type ITempVideoItem = ITempItem & { medium: IVideo };
/**
 * Can Delete, Upload and View Images for a Partner
 */
@Component({
  components: {
    ConfirmActionDialog,
    MediaGalleryFullscreen,
    MediaGalleryItem
  }
})
class MediaGallery<
  T extends IHasPartnerId & IHasImages & IHasImageIds & IHasId & HasUpdateMedia<T> & Partial<IHasVideoIds & IHasVideos>
> extends Vue {
  readonly MediaGalleryItemType = MediaGalleryItemType;

  @Prop({ required: true })
  value!: T;

  @Prop({ default: false })
  readonly!: boolean;

  isDraggingOver = false;
  selectedImages: string[] = [];
  selectedVideos: string[] = [];
  isDeleteDialogActive = false;
  isUploading = false;
  isDeleteLoading = false;
  isUpdatingImages = false;
  isUpdatingVideos = false;
  isFullscreenActive = false;

  fullscreenIndex = 0;

  tempImages: ITempImageItem[] = [];

  tempVideos: ITempVideoItem[] = [];

  get images(): IPartnerImage[] {
    return this.value.images || [];
  }

  get videos(): IVideo[] {
    return this.value.videos || [];
  }

  get fullscreenMedia(): MediaGalleryFullscreenItem[] {
    return [
      ...this.images.map(medium => ({ type: MediaGalleryItemType.IMAGE as const, medium })),
      ...this.videos.map(medium => ({ type: MediaGalleryItemType.VIDEO as const, medium }))
    ];
  }

  get currentFullscreenItem() {
    return this.fullscreenMedia[this.fullscreenIndex];
  }

  get hasMedia() {
    const imgCount = (this.images || []).length;
    const vidCount = (this.videos || []).length;
    const previewCount = this.tempVideos.length + this.tempImages.length;
    return imgCount + vidCount + previewCount > 0;
  }

  addTempImage(file: File) {
    const img = new PartnerImage({
      partnerId: this.value.partnerId,
      file: file,
      url: URL.createObjectURL(file)
    });

    this.tempImages.push({
      _tempKey: this.createTempKey(MediaGalleryItemType.IMAGE),
      medium: img
    });
  }

  addTempVideo(file: File) {
    const video = new Video({
      partnerId: this.value.partnerId,
      file: file
    });

    this.tempVideos.push({
      _tempKey: this.createTempKey(MediaGalleryItemType.VIDEO),
      medium: video
    });
  }

  private createTempKey(prefix: MediaGalleryItemType) {
    const randomPart = Math.random().toString(36);
    return `${prefix}-${randomPart}`;
  }

  removeTempImage(tempKey: string) {
    const index = this.tempImages.findIndex(i => i._tempKey === tempKey);
    if (index < 0) return;
    const [removed] = this.tempImages.splice(index, 1);
    try {
      URL.revokeObjectURL(removed.medium.url);
    } catch {
      // ignore
    }
  }

  removeTempVideo(tempKey: string) {
    const index = this.tempVideos.findIndex(v => v._tempKey === tempKey);
    if (index < 0) return;
    const [removed] = this.tempVideos.splice(index, 1);
    try {
      const preview = removed?.medium?._file;
      if (preview) URL.revokeObjectURL(URL.createObjectURL(preview));
    } catch {
      // ignore
    }
  }

  async uploadFile(files: File[]) {
    if (files.length === 0) return;
    // Create temp tiles only; MediaGalleryItem handles the actual create/upload
    this.isUploading = true;
    for (const f of files) {
      this.addTempImage(f);
    }
    this.isUploading = false;
  }

  handleDelete() {
    if (this.selectedImages.length === 0 && this.selectedVideos.length === 0) return;
    this.deleteImages(this.selectedImages);
    this.deleteVideos(this.selectedVideos);
    this.selectedImages = [];
    this.selectedVideos = [];
    this.isDeleteDialogActive = false;
  }

  isSelected(kind: MediaGalleryItemType.IMAGE | MediaGalleryItemType.VIDEO, id: string) {
    return kind === MediaGalleryItemType.IMAGE ? this.selectedImages.includes(id) : this.selectedVideos.includes(id);
  }

  toggleSelection(kind: MediaGalleryItemType.IMAGE | MediaGalleryItemType.VIDEO, id: string) {
    const selected = kind === MediaGalleryItemType.IMAGE ? this.selectedImages : this.selectedVideos;
    const index = selected.indexOf(id);
    if (index > -1) {
      selected.splice(index, 1);
    } else {
      selected.push(id);
    }
  }

  onTouchStart(kind: MediaGalleryItemType.IMAGE | MediaGalleryItemType.VIDEO, id: string) {
    this.toggleSelection(kind, id);
  }

  onTouchEnd() {
    // no-op
  }

  async download() {
    const imagesToDownload = this.selectedImages.length
      ? this.images?.filter(img => this.selectedImages.includes(img.id))
      : this.images;

    await Promise.all((imagesToDownload ?? []).map(i => i.download()));
  }

  openFullscreen(kind: MediaGalleryItemType.IMAGE | MediaGalleryItemType.VIDEO, id: string) {
    const idx = this.fullscreenMedia.findIndex(m => m.type === kind && m.medium.id === id);
    this.fullscreenIndex = Math.max(0, idx);
    this.isFullscreenActive = true;
  }

  closeFullscreen() {
    this.isFullscreenActive = false;
  }

  async handleFullscreenDelete() {
    const cur = this.currentFullscreenItem;
    if (!cur) return;
    if (cur.type === MediaGalleryItemType.IMAGE) {
      await this.deleteImages([cur.medium.id]);
    } else {
      await this.deleteVideos([cur.medium.id]);
    }
    this.$nextTick(() => {
      if (this.fullscreenIndex >= this.fullscreenMedia.length) {
        this.fullscreenIndex = Math.max(0, this.fullscreenMedia.length - 1);
      }
      if (!this.fullscreenMedia.length) {
        this.closeFullscreen();
      }
    });
  }

  async downloadCurrent() {
    const cur = this.currentFullscreenItem;
    if (!cur) return;
    if (cur.type === MediaGalleryItemType.IMAGE) {
      const img = (this.images || []).find(i => i.id === cur.medium.id);
      if (img) {
        await img.download();
      }
      return;
    }

    const v = (this.videos || []).find(x => x.id === cur.medium.id);
    const url = v?.src || v?.muxPlayerSrc;
    if (url) {
      window.open(url, "_blank");
    }
  }

  openMediaCapture() {
    const input = this.$refs.mediaCaptureInput as HTMLInputElement | undefined;
    if (!input) return;
    input.click();
  }

  async onMediaCaptured(e: Event) {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    input.value = "";
    if (files.length === 0) return;
    this.handleSelectedFiles(files);
  }

  onDragOver(e: DragEvent) {
    if (this.readonly) return;
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
    this.isDraggingOver = true;
  }

  onDragLeave(e: DragEvent) {
    if (this.readonly) return;
    const current = e.currentTarget as HTMLElement | null;
    const related = e.relatedTarget as Node | null;
    if (!related || !current?.contains(related)) {
      this.isDraggingOver = false;
    }
  }

  onGalleryDrop(e: DragEvent) {
    if (this.readonly) return;
    this.isDraggingOver = false;

    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    this.handleSelectedFiles(files);
  }

  handleSelectedFiles(files: File[]) {
    if (this.readonly) return;

    const imageFiles: File[] = [];
    const videoFiles: File[] = [];

    for (const f of files) {
      if (f.type?.startsWith("video/")) {
        videoFiles.push(f);
      } else if (f.type?.startsWith("image/")) {
        imageFiles.push(f);
      }
    }

    if (!imageFiles.length && !videoFiles.length) {
      this.$toast.error(this.$t("components.DropField.filesNotAllowed"));
      return;
    }

    // Kick off image uploads (parallel) + video uploads (queued) concurrently
    if (imageFiles.length) {
      this.uploadFile(imageFiles).catch(error => {
        handleError(error);
      });
    }

    for (const vf of videoFiles) {
      this.addTempVideo(vf);
    }
  }

  async handleImagesCreated(uploadedImages: IPartnerImage[]) {
    if (uploadedImages.length === 0) return;

    this.isUpdatingImages = true;
    if (!this.value.imageIds) {
      this.value.imageIds = [];
    }
    const newImageIds = [...this.value.imageIds, ...uploadedImages.map(img => img.id)];
    this.value.imageIds.splice(0);
    this.value.imageIds.push(...newImageIds);
    await this.debounceUpdateIds();
    this.isUpdatingImages = false;
  }

  async deleteImages(imageIds: string[]) {
    if (imageIds.length === 0) return;

    this.isDeleteLoading = true;

    if (!this.value.imageIds) {
      this.value.imageIds = [];
    }
    const newImageIds = this.value.imageIds.filter(id => !imageIds.includes(id));
    this.value.imageIds.splice(0);
    this.value.imageIds.push(...newImageIds);
    await this.debounceUpdateIds();
    this.isDeleteLoading = false;
  }

  async deleteVideos(videoIds: string[]) {
    if (videoIds.length === 0) return;
    this.isDeleteLoading = true;
    this.isUpdatingVideos = true;

    const v = this.value;
    if (!v.videoIds) {
      v.videoIds = [];
    }
    const newVideoIds = (v.videoIds as string[]).filter((id: string) => !videoIds.includes(id));
    v.videoIds.splice(0);
    v.videoIds.push(...newVideoIds);

    await this.debounceUpdateIds();
    this.isDeleteLoading = false;
    this.isUpdatingVideos = false;
  }

  onTempVideoCreated(tempKey: string, videoId: string) {
    const v = this.value;
    if (!v.videoIds) v.videoIds = [];
    if (!v.videoIds.includes(videoId)) {
      v.videoIds.push(videoId);
    }

    this.removeTempVideo(tempKey);
    this.debounceUpdateIds();
  }

  onTempImageCreated(tempKey: string, imageId: string) {
    const v = this.value;
    if (!v.imageIds) v.imageIds = [];
    if (!v.imageIds.includes(imageId)) {
      v.imageIds.push(imageId);
    }

    this.removeTempImage(tempKey);
    this.debounceUpdateIds();
  }

  debounceUpdateIds = debounce(this.updateIds, 500);

  async updateIds() {
    try {
      this.isUpdatingImages = true;
      this.isUpdatingVideos = true;

      const dto: Partial<IHasImageIds & IHasVideoIds> = {};

      dto.imageIds = [...(this.value.imageIds ?? [])];
      dto.videoIds = [...(this.value.videoIds ?? [])];

      await this.value.updatePartial(dto);
    } catch (error) {
      handleError(error);
    } finally {
      this.isUpdatingImages = false;
      this.isUpdatingVideos = false;
      this.isDeleteLoading = false;
    }
  }

  @Watch("isUpdatingImages")
  @Watch("isUpdatingVideos")
  @Watch("tempImages")
  @Watch("tempVideos")
  emitUpdateLoading() {
    const isLoading =
      this.isUpdatingImages || this.isUpdatingVideos || this.tempImages.length > 0 || this.tempVideos.length > 0;
    this.$emit("update:loading", isLoading);
  }
}

export default MediaGallery;
